'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UserRole = 'SUPER_ADMIN' | 'ADMIN' | 'customer' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roleLoading: boolean;
  userRole: UserRole;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SUPER_ADMIN_EMAIL = 'superadmin@sellnex.net';

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Return safe defaults instead of throwing — prevents SSR crash in nested layouts
    return {
      user: null,
      session: null,
      loading: true,
      roleLoading: true,
      userRole: null,
      isAdmin: false,
      isSuperAdmin: false,
      signIn: async () => ({ error: null }),
      signUp: async () => ({ error: null }),
      signOut: async () => {},
    };
  }
  return ctx;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function resolveRole(supabase: ReturnType<typeof createClient>, user: User): Promise<UserRole> {
  // 1. Email fail-safe
  if (user.email === SUPER_ADMIN_EMAIL) return 'SUPER_ADMIN';

  // 2. app_metadata (set by DB trigger / admin)
  const metaRole = user.app_metadata?.role as string | undefined;
  if (metaRole) {
    const upper = metaRole.toUpperCase();
    if (upper === 'SUPER_ADMIN') return 'SUPER_ADMIN';
    if (upper === 'ADMIN') return 'ADMIN';
  }

  // 3. DB profile
  try {
    const { data } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', user.id)
      .maybeSingle();

    if (data?.role) {
      const r = String(data.role).toUpperCase();
      if (r === 'SUPER_ADMIN') return 'SUPER_ADMIN';
      if (r === 'ADMIN') return 'ADMIN';
    }
  } catch {
    // ignore DB errors — fall through to default
  }

  return 'customer';
}

function clearLocalSession() {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('sb-') || k.includes('supabase'))) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole>(null);

  const supabase = createClient();

  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';
  const isSuperAdmin = userRole === 'SUPER_ADMIN';

  // ── Auth state listener ──────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const handleUser = async (sessionUser: User | null, newSession: Session | null) => {
      if (!mounted) return;
      setSession(newSession);
      setUser(sessionUser);

      if (sessionUser) {
        setRoleLoading(true);
        const role = await resolveRole(supabase, sessionUser);
        if (mounted) {
          setUserRole(role);
          setRoleLoading(false);
        }
      } else {
        setUserRole(null);
        setRoleLoading(false);
      }
    };

    // Subscribe to auth changes
    let subscription: { unsubscribe: () => void } = { unsubscribe: () => {} };
    try {
      const { data } = supabase.auth.onAuthStateChange(async (event, newSession) => {
        if (!mounted) return;

        if (event === 'TOKEN_REFRESHED' && !newSession) {
          clearLocalSession();
          setSession(null);
          setUser(null);
          setUserRole(null);
          setRoleLoading(false);
          setLoading(false);
          return;
        }

        await handleUser(newSession?.user ?? null, newSession);
        if (mounted) setLoading(false);
      });
      subscription = data.subscription;
    } catch (e) {
      // Guard against @supabase/auth-js internal i18n.translations crash
      console.warn('Auth state change subscription error (non-fatal):', e instanceof Error ? e.message : e);
    }

    // Restore session backup (prevents auth loop after login redirect)
    const init = async () => {
      let restoredFromBackup = false;
      try {
        const backup = sessionStorage.getItem('sb-login-session');
        if (backup) {
          sessionStorage.removeItem('sb-login-session');
          const { access_token, refresh_token } = JSON.parse(backup);
          if (access_token && refresh_token) {
            const { error: setErr } = await supabase.auth.setSession({ access_token, refresh_token });
            if (!setErr) {
              // onAuthStateChange will fire and call handleUser — skip getSession to avoid double refresh
              restoredFromBackup = true;
            }
          }
        }
      } catch {
        // ignore
      }

      if (restoredFromBackup) {
        // Let onAuthStateChange handle the rest; just ensure loading is cleared
        if (mounted) setLoading(false);
        return;
      }

      try {
        const { data: { session: initial }, error } = await supabase.auth.getSession();

        if (error) {
          if (mounted) {
            setSession(null);
            setUser(null);
            setUserRole(null);
            setRoleLoading(false);
            setLoading(false);
          }
          return;
        }

        await handleUser(initial?.user ?? null, initial);
        if (mounted) setLoading(false);
      } catch (e) {
        // Guard against @supabase/auth-js internal i18n.translations crash
        console.warn('Auth getSession error (non-fatal):', e instanceof Error ? e.message : e);
        if (mounted) {
          setSession(null);
          setUser(null);
          setUserRole(null);
          setRoleLoading(false);
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // ── Auth methods ─────────────────────────────────────────────────────────

  const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error?.message ?? null };
    } catch (e) {
      // Guard against @supabase/auth-js internal i18n.translations crash
      return { error: e instanceof Error ? e.message : 'Sign in failed' };
    }
  };

  const signUp = async (email: string, password: string, fullName: string): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName, role: 'customer' } },
      });
      return { error: error?.message ?? null };
    } catch (e) {
      // Guard against @supabase/auth-js internal i18n.translations crash
      return { error: e instanceof Error ? e.message : 'Sign up failed' };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore
    }
    clearLocalSession();
  };

  return (
    <AuthContext.Provider value={{
      user, session, loading, roleLoading,
      userRole, isAdmin, isSuperAdmin,
      signIn, signUp, signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
